import { Component, Input, OnInit, ViewChildren, QueryList } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatNavList } from '@angular/material/list';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { FormBuilder, FormsModule } from '@angular/forms';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { BreadcrumbsComponent } from '../breadcrumbs/breadcrumbs.component';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { ScopeService } from '../../services/scope.service';
import { GlobalVariables } from '../../constants/global-constants';
import { UtilitiesService } from '../../services/utilities.service';
import { ThemeSelectorComponent } from "../theme-selector/theme-selector.component";
import { CookieService } from 'ngx-cookie-service';
import { filter, lastValueFrom, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-side-navigation-menu',
  standalone: true,
  imports: [
    CommonModule,
    BreadcrumbsComponent,
    MatSidenavModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatNavList,
    MatFormFieldModule,
    MatExpansionModule,
    MatButtonModule,
    RouterModule,
    FormsModule,
    MatIconModule,
    ThemeSelectorComponent
  ],
  templateUrl: './side-navigation-menu.component.html',
  styleUrl: './side-navigation-menu.component.scss',
})
export class SideNavigationMenuComponent implements OnInit {
  @Input() externalMenu: menu[] | null = null;
  @ViewChildren(MatExpansionPanel) expansionPanels!: QueryList<MatExpansionPanel>;

  settingsText = $localize`Settings`;

  // State variables
  isPinned = true;

  // Layout constants
  readonly expandedWidth = 240;
  readonly collapsedWidth = 65; // Enough for icons
  contentMargin = this.expandedWidth;

  width = this.expandedWidth;

  // Theme & content
  options = this._formBuilder.group({
    bottom: 0,
    fixed: false,
    top: 0,
  });
  scope = [];
  showSettings = false;

  // Selection states
  dataTableSelected = false;
  dataQueueSelected = false;
  dataAdminSelected = false;
  dataTitanSelected = false;
  dataToolsSelected = false;
  dataFdIdSelected = false;
  dataOnboardingSelected = false;

  tableURLS = GlobalVariables.tableURL;
  queueURLS = GlobalVariables.queueURL;
  adminURLS = GlobalVariables.adminURL;
  titanURLS = GlobalVariables.titanUrl;
  toolsURLS = GlobalVariables.toolsURL;
  fdIdURLS = GlobalVariables.fdIdURL;
  onboardingURLS = GlobalVariables.onboardingURL;
  clientId = 0;

  menuList: menu[] = [];

  transition = 'width 0.3s ease-in-out';
  isDarkMode = false;
  private destroy$ = new Subject<void>();
  isOnboarding: boolean = false;
  isFdUser: boolean = false;

  constructor(
    private _formBuilder: FormBuilder,
    private scopeService: ScopeService,
    private router: Router,
    private cookieService: CookieService,
    public utilities: UtilitiesService,
  ) { }

  get isExpanded(): boolean {
    return this.isPinned;
  }

  toggleMenu() {
    this.isPinned = !this.isPinned;
    this.updateLayout();
  }

  expandMenu() {
    if (!this.isExpanded) {
      this.isPinned = true;
      this.updateLayout();
    }
  }


  updateLayout() {
    if (this.isExpanded) {
      this.width = this.expandedWidth;
      this.contentMargin = this.expandedWidth;
      this.utilities.showLogo = false;
    } else {
      this.width = this.collapsedWidth;
      this.contentMargin = this.collapsedWidth;
      this.utilities.showLogo = true;
      this.closeAllSubMenus();
    }
  }

  closeAllSubMenus() {
    if (this.expansionPanels) {
      this.expansionPanels.forEach(panel => panel.close());
    }
  }

  async ngOnInit(): Promise<void> {
    this.isOnboarding = this.utilities.companyStatusValue === 'Onboarding' ? true : false;
    this.isFdUser = this.utilities.isFdUserValue;
    this.clientId = this.utilities.clientId;

    await this.loadScopes();
    this.utilities.scopeMenu();
    this.menuList = this.externalMenu ?? this.utilities.filteredMenuItem;
    this.menuList = this.filterMenuForRole(this.menuList, this.isFdUser);

    this.isDarkMode = localStorage.getItem('theme') == GlobalVariables.darkTheme;
    this.utilities.isDarkTheme.set(this.isDarkMode);
    this.showSettings = this.scopeService.Scope?.some((x: any) => x.resource === 'settings') ?? false;

    if (!this.externalMenu) {
      this.router.events
        .pipe(
          filter((e): e is NavigationEnd => e instanceof NavigationEnd),
          takeUntil(this.destroy$)
        )
        .subscribe((e) => {
          const url = e.urlAfterRedirects || e.url;
          this.checkActiveRoute(url);
        });
      // Initial check
      this.checkActiveRoute(this.router.url);
    }

    // Initial layout
    this.updateLayout();
  }

  checkActiveRoute(url: string) {
    const urlSegments = url.split('/');
    if (urlSegments.length > 3) {
      const section = urlSegments[3];
      const path = '/' + (urlSegments[4] ?? '');

      this.dataTableSelected = section === 'tables' && this.tableURLS.includes(path);
      this.dataQueueSelected = section === 'queue' && this.queueURLS.includes(path);

      this.dataAdminSelected = section === 'admin' && this.adminURLS.includes(path);
      this.dataTitanSelected = section === 'titan' && this.titanURLS.includes(path);
      this.dataToolsSelected = section === 'tools' && this.toolsURLS.includes(path);
      this.dataFdIdSelected = section === 'fdid' && this.fdIdURLS.includes(path);
      this.dataOnboardingSelected = section === 'onboarding' && this.onboardingURLS.includes(path);
    } else {
      // Clear all
      this.dataTableSelected = this.dataQueueSelected = this.dataAdminSelected =
        this.dataTitanSelected = this.dataToolsSelected = this.dataFdIdSelected =
        this.dataOnboardingSelected = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  themeSelected(theme: string) {
    if (theme == GlobalVariables.darkTheme) {
      this.isDarkMode = true;
      this.utilities.isDarkTheme.set(true);
    } else {
      this.isDarkMode = false;
      this.utilities.isDarkTheme.set(false);
    }
  }

  private async loadScopes() {
    const token = this.cookieService.get('token');
    if (token) {
      try {
        const res: any = await lastValueFrom(this.scopeService.fetchScopes(this.utilities.clientId));
        this.scopeService.Scope = res.scopes;
      } catch (error) {
        console.warn(error);
        this.scopeService.Scope = [];
      }
    } else {
      this.scopeService.Scope = [];
    }
  }

  private filterMenuForRole(items: menu[], isFdUser: boolean): menu[] {
    if (isFdUser) return items;

    const isTables = (m: menu) =>
      m.name?.trim().toLowerCase() === 'tables' ||
      m.url?.trim().toLowerCase().startsWith('/tables');
    const recurse = (arr: menu[]): menu[] =>
      arr
        .filter(m => !isTables(m))
        .map(m => ({
          ...m,
          subMenu: m.subMenu ? recurse(m.subMenu) : []
        }))
        .filter(m => (m.subMenu?.length ?? 0) > 0 || !isTables(m));

    return recurse(items);
  }
}


interface menu {
  name: string;
  url: string;
  icon: string;
  subMenu: menu[];
}
